import { Button, Col, Input, Label, Modal, Row, FormGroup } from 'reactstrap';
import { useEffect, useState, useCallback } from 'react';
import { pdfjs } from 'react-pdf';
import SignatureCanvas from 'react-signature-canvas';
import { PDFDocument, StandardFonts, rgb } from 'pdf-lib';
import moment from 'moment';
import { toast } from 'react-toastify';
import HireDocuments from '../../../services/HireDocuments';

pdfjs.GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.js`;

type SignDocumentModalProps = {
  isOpen: boolean;
  documentConfig: any;
  token: string;
  currentUser: any;
  toggleModal: (e?: any) => void;
  saveModal: (e?: any) => void;
};

const SignDocumentModal = ({
  isOpen,
  documentConfig,
  token,
  currentUser,
  toggleModal,
  saveModal,
}: SignDocumentModalProps) => {
  const [loading, setLoading] = useState(false);
  const [isSign, setIsSign] = useState(false);
  const [sigPad, setSigPad] = useState<any>();
  const [pdf, setPdf] = useState('');
  const [pdfSignedBlob, setPdfSignedBlob] = useState<Blob | null>(null);
  const [signature, setSignature] = useState<string | null>(null);

  const createPdf = useCallback(async () => {
    if (!documentConfig || !documentConfig.file) return;

    try {
      const arrayBuffer = await fetch(documentConfig.file).then((res) => res.arrayBuffer());
      const pdfDoc = await PDFDocument.load(arrayBuffer);
      const timesRomanFont = await pdfDoc.embedFont(StandardFonts.TimesRoman);
      const helveticaFont = await pdfDoc.embedFont(StandardFonts.Helvetica);
      const helveticaObliqueFont = await pdfDoc.embedFont(StandardFonts.HelveticaOblique);
      const pages = pdfDoc.getPages();

      let signaturePage;

      if (documentConfig.signPage === -1) {
        signaturePage = pdfDoc.addPage();
        pages.push(signaturePage);
        const config = documentConfig;
        const { width } = signaturePage.getSize();
        const employeeName = currentUser?.userName || '_________________________';
        const documentTitle = config.title || 'the document';

        let acknowledgementText = config.acknowledgementText.text;
        acknowledgementText = acknowledgementText.replace('{employeeName}', employeeName);
        acknowledgementText = acknowledgementText.replace('{documentTitle}', documentTitle);

        signaturePage.drawText(acknowledgementText, {
          ...config.acknowledgementText,
          font: helveticaFont,
          maxWidth: width - config.acknowledgementText.x * 2,
          lineHeight: 15,
        });

        const lineOptions = { thickness: 0.5, color: rgb(0.2, 0.2, 0.2) };
        const labelOptions = { font: helveticaObliqueFont, size: 9, color: rgb(0.3, 0.3, 0.3) };

        signaturePage.drawText(config.employeeBlock.label.text, { ...config.employeeBlock.label, ...labelOptions });
        signaturePage.drawLine({ ...config.employeeBlock.signature.line, ...lineOptions });
        signaturePage.drawText(config.employeeBlock.printedName.label.text, {
          ...config.employeeBlock.printedName.label,
          ...labelOptions,
        });
        signaturePage.drawLine({ ...config.employeeBlock.printedName.line, ...lineOptions });
        signaturePage.drawText(config.employeeBlock.date.label.text, {
          ...config.employeeBlock.date.label,
          ...labelOptions,
        });
        signaturePage.drawLine({ ...config.employeeBlock.date.line, ...lineOptions });

        // TODO: Uncomment if director block is necessary in the future
        // signaturePage.drawText(config.directorBlock.label.text, { ...config.directorBlock.label, ...labelOptions });
        // signaturePage.drawLine({ ...config.directorBlock.signatureLine, ...lineOptions });
        // signaturePage.drawText(config.directorBlock.nameLabel.text, {
        //   ...config.directorBlock.nameLabel,
        //   ...labelOptions,
        // });
        // signaturePage.drawText(config.directorBlock.titleLabel.text, {
        //   ...config.directorBlock.titleLabel,
        //   ...labelOptions,
        // });
      } else {
        signaturePage = pages[documentConfig.signPage || 0];
      }

      if (signaturePage && sigPad) {
        const staffName = currentUser?.userName || '';
        const currentDate = moment().format('MM/DD/YYYY');

        if (!sigPad.isEmpty()) {
          const signatureImage = sigPad.getTrimmedCanvas().toDataURL('image/png');
          if (!signature) setSignature(signatureImage);
          const pngImage = await pdfDoc.embedPng(signatureImage);
          const signatureCoords =
            documentConfig.employeeBlock?.signature?.coordinates ||
            documentConfig.signature?.coordinates ||
            documentConfig.coordinates;
          signaturePage.drawImage(pngImage, signatureCoords);
        }

        if (documentConfig.signPage === -1) {
          if (documentConfig.employeeBlock?.printedName?.coordinates) {
            signaturePage.drawText(staffName, {
              ...documentConfig.employeeBlock.printedName.coordinates,
              font: timesRomanFont,
              color: rgb(0, 0, 0),
            });
          }

          if (documentConfig.employeeBlock?.date?.coordinates) {
            signaturePage.drawText(currentDate, {
              ...documentConfig.employeeBlock.date.coordinates,
              font: timesRomanFont,
              color: rgb(0, 0, 0),
            });
          }
        } else {
          if (documentConfig.coordinatesRectangle) {
            signaturePage.drawRectangle({
              ...documentConfig.coordinatesRectangle,
            });
          }

          const nameConfig = documentConfig.coordinatesName;
          if (nameConfig) {
            const namePage = pages[documentConfig.signNamePage || documentConfig.signPage || 0];
            namePage.drawText(staffName, { ...nameConfig, font: timesRomanFont, color: rgb(0, 0, 0) });
          }

          const dateConfig = documentConfig.coordinatesDate;
          if (dateConfig) {
            const datePage = pages[documentConfig.signDatePage || documentConfig.signPage || 0];
            datePage.drawText(currentDate, { ...dateConfig, font: timesRomanFont, color: rgb(0, 0, 0) });
          }

          if (documentConfig.coordinatesNames) {
            for (const n of documentConfig.coordinatesNames) {
              pages[n.page || 0].drawText(staffName, { ...n.coordinates, font: timesRomanFont, color: rgb(0, 0, 0) });
            }
          }
        }
      }

      const pdfBytes = await pdfDoc.save();
      const blob = new Blob([pdfBytes as any], { type: 'application/pdf' });
      setPdfSignedBlob(blob);
      setPdf(URL.createObjectURL(blob));
    } catch (e) {
      console.error('Failed to create PDF:', e);
      toast.error('Could not load the document for signing.');
    }
  }, [documentConfig, sigPad, currentUser, signature]);

  useEffect(() => {
    if (!isOpen) {
      setPdf('');
      setIsSign(false);
      setSigPad(undefined);
      setSignature(null);
      setPdfSignedBlob(null);
    } else {
      createPdf();
    }
  }, [isOpen, createPdf]);

  const handleSubmit = async (e: React.FormEvent) => {
    if (e?.preventDefault) e?.preventDefault();
    if (loading || !pdfSignedBlob || !signature) {
      toast.warn('Please sign the document before saving.');
      return;
    }
    setLoading(true);

    const body = {
      type: documentConfig.type,
      userId: currentUser?.userId || '',
      userName: currentUser?.userName || '',
      userToken: token,
      pdfUrl: documentConfig.file,
      pdfSigned: pdfSignedBlob,
      signature,
      options: { ...documentConfig, signPng: signature, signatureDate: moment().format('YYYY-MM-DD') },
    };

    try {
      await HireDocuments('').create(body);
      toast.success(`Saved successfully!`);
      saveModal(documentConfig.type);
      toggleModal();
    } catch (error: any) {
      console.log(error);
      const message = String(error?.response?.data?.message || 'Create error!');
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <Modal className="modal-dialog-centered" size="xl" isOpen={isOpen} toggle={toggleModal}>
      <div className="modal-header pb-1">
        <h5 className="modal-title">{documentConfig?.title || 'Sign Document'}</h5>
        <button aria-label="Close" className="close" type="button" onClick={toggleModal}>
          <span aria-hidden={true}>×</span>
        </button>
      </div>
      <div className="modal-body pt-0">
        <Row className="mt-2">
          <Col lg="12" className="mt-4" style={{ height: '500px', overflow: 'auto', border: '1px solid #dee2e6' }}>
            {pdf ? (
              <iframe
                src={pdf}
                style={{ minHeight: '100%', width: '100%', border: 'none' }}
                title={documentConfig?.title}
              />
            ) : (
              <p>Loading document...</p>
            )}
          </Col>
          <Col lg="12" className="mt-4">
            <Label>
              <strong>Signature:</strong>
            </Label>
            <div className="d-flex flex-wrap align-items-center">
              <FormGroup check className="mr-3">
                <Input
                  className="form-check-input"
                  type="checkbox"
                  id="to-sign"
                  checked={isSign}
                  onChange={(e) => setIsSign(e.target.checked)}
                />
                <Label className="form-check-label" htmlFor="to-sign">
                  I hereby acknowledge and agree that my electronic signature on this document is the legal equivalent
                  of my handwritten signature.
                </Label>
              </FormGroup>
              {isSign && (
                <Col lg="12" className="mt-4">
                  <div>
                    <SignatureCanvas
                      penColor="blue"
                      canvasProps={{
                        width: 500,
                        height: 200,
                        className: 'sig-canvas',
                        style: { border: '1px dashed #000' },
                      }}
                      ref={(ref) => setSigPad(ref)}
                      onEnd={createPdf}
                    />
                  </div>
                  <Button onClick={() => sigPad?.clear()} color="warning" size="sm" className="mt-1 mb-2">
                    Clear
                  </Button>
                </Col>
              )}
            </div>
          </Col>
        </Row>
        <hr />
        <Row>
          <Col>
            <Button color="success" size="md" onClick={handleSubmit} disabled={loading || !isSign || !signature}>
              {loading ? 'Saving...' : 'Save & Submit'}
            </Button>
            <Button color="secondary" size="md" onClick={toggleModal} className="ml-2">
              Cancel
            </Button>
          </Col>
        </Row>
      </div>
    </Modal>
  );
};

export default SignDocumentModal;
